import { useCallback, useEffect } from 'react';
import UsageTracker from '../../../utils/UsageTracker';
import { SCREENS } from '../../../constants/Tracking';
import { MY_SAVED_ITEMS_PAGE_SIZE, PlaylistCard, PlaylistCreatedAndAssignedByLabel, SavedItemsPagedList } from '../../savedItems';
import { MyPlaylistsTabNavigation, useMyPlaylistsFilters, useMyPlaylistsParams, useMyPlaylists, MyPlaylistsTabNavigationMobile, MY_PLAYLISTS_SORT_MAP } from '..';
import { useDisplayedData } from '../../table';
import FormattedMessage from 'I18n/components/FormattedMessage';
import styled, { theme, useMediaQuery } from 'academy-common-lib/utils/Styling';
import { FILTER_TYPES } from '../../../constants/TopFilterConstants';
import { jsx as _jsx } from "react/jsx-runtime";
const useMyPlaylistsTab = () => {
  const {
    page: pageParam,
    tab
  } = useMyPlaylistsParams();
  const {
    searchFilter,
    sortBy
  } = useMyPlaylistsFilters();
  const page = Number(pageParam) || 1;
  const hasFilters = !!searchFilter && !!sortBy;
  const {
    sortProperty,
    sortOrder
  } = MY_PLAYLISTS_SORT_MAP[sortBy || (searchFilter ? 'RELEVANCE_DESCENDING' : 'RECENTLY_ACCESSED_DESCENDING')];
  const {
    totalPlaylists,
    playlists,
    playlistsLoading,
    playlistsError
  } = useMyPlaylists({
    tab,
    limit: MY_SAVED_ITEMS_PAGE_SIZE,
    page,
    sortOrder,
    sortProperty,
    query: searchFilter
  });
  return {
    totalPlaylists,
    tab,
    page,
    playlists,
    playlistsLoading,
    playlistsError,
    hasFilters,
    searchFilter,
    sortBy
  };
};
const ArchivedMessage = styled(FormattedMessage).attrs({
  message: 'learningCenter.myPlaylists.archived'
}).withConfig({
  displayName: "MyPlaylistsTabs__ArchivedMessage",
  componentId: "b93q7o-0"
})(["&::before{content:'(';}&::after{content:')';}text-transform:none;margin-top:4px;opacity:0.5;color:", ";"], theme.colors.OLAF);
export const MyPlaylistsTabs = () => {
  const {
    hasFilters,
    playlists,
    page,
    totalPlaylists,
    playlistsLoading,
    playlistsError,
    tab,
    searchFilter,
    sortBy
  } = useMyPlaylistsTab();
  const hasSearchFilter = !!(searchFilter && searchFilter.trim().length);
  const {
    isMobile
  } = useMediaQuery();

  // displays only initial data dependent on tab, page and filters in order
  // to render ghosts
  const displayedData = useDisplayedData({
    playlists,
    totalPlaylists
  }, {
    loading: playlistsLoading,
    initialOnly: true,
    parameters: {
      tab,
      page,
      searchFilter,
      sortBy
    }
  });
  useEffect(() => {
    UsageTracker.track('pageView', {
      screen: SCREENS.MY_PLAYLISTS
    });
  }, []);
  const isGhost = useCallback(playlistId => playlists !== null && !playlists.some(p => p.id === playlistId), [playlists]);
  const isArchived = useCallback(playlistId => tab !== 'archived' && playlists !== null && playlists.some(p => p.id === playlistId && p.hiddenForOwner), [playlists, tab]);
  const isRequesting = playlistsLoading || !displayedData;
  const {
    playlists: playlistsToDisplay,
    totalPlaylists: displayedTotalPlaylists
  } = displayedData || {};
  return /*#__PURE__*/_jsx(SavedItemsPagedList, {
    alwaysShowFilters: hasSearchFilter,
    alwaysShowResultsStatusBar: true,
    error: playlistsError,
    filters: [FILTER_TYPES.SEARCH, FILTER_TYPES.SORT_BY_MY_PLAYLISTS],
    hasError: !!playlistsError,
    hasItems: !!displayedTotalPlaylists && displayedTotalPlaylists > 0 || hasFilters // having filters means there are playlists
    ,
    hideFilterLabel: true,
    isLoadingPageOnDemand: true,
    isRequesting: isRequesting,
    itemKey: "myPlaylists",
    list: playlistsToDisplay && playlistsToDisplay.map(({
      id
    }, index) => /*#__PURE__*/_jsx(PlaylistCard, {
      body: tab !== 'created-by-me' && tab !== 'archived' && /*#__PURE__*/_jsx(PlaylistCreatedAndAssignedByLabel, {
        playlistId: id,
        style: {
          opacity: isArchived(id) ? 0.5 : 1,
          transform: 'translateY(-10px)'
        }
      }),
      isGhost: isGhost(id) || isArchived(id),
      playlistId: id,
      thumbnailHeader: isArchived(id) && /*#__PURE__*/_jsx(ArchivedMessage, {})
    }, index)),
    markerKey: "MY_PLAYLISTS",
    mobileResultsActions: !hasSearchFilter && /*#__PURE__*/_jsx(MyPlaylistsTabNavigationMobile, {}),
    navigation: !isMobile && /*#__PURE__*/_jsx(MyPlaylistsTabNavigation, {}),
    page: page,
    totalCount: displayedTotalPlaylists || 0
  });
};